import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeIn } from "@/components/Reveal";
import { useSiteGallery } from "@/hooks/useSiteContent";
import { IMAGES, GALLERY_FILTERS } from "@/data/content";

export default function GalleriaPage() {
  const { items } = useSiteGallery();
  const [filter, setFilter] = useState("tutte");
  const [selected, setSelected] = useState(null);

  const shown = items.filter((g) => filter === "tutte" || g.cat === filter);

  return (
    <>
      <PageHero
        eyebrow="Galleria"
        lines={["Il cantiere", "in immagini"]}
        description="I nostri lavori eseguiti: perforazioni, opere strutturali, versanti sistemati, opere in quota e restauri."
        image={IMAGES.drillNight}
      />

      <section className="py-20 lg:py-28" data-testid="galleria-grid-section">
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-8">
          <div className="flex flex-wrap gap-3" data-testid="galleria-filters">
            {GALLERY_FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`gallery-filter-${f.id}`}
                className={`border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-all duration-300 ${
                  filter === f.id
                    ? "border-brand-ice bg-brand-ice text-brand-teal"
                    : "border-white/20 text-white/70 hover:border-brand-cyan hover:text-brand-ice"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <motion.div layout className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="galleria-grid">
            <AnimatePresence mode="popLayout">
              {shown.map((g) => (
                <motion.figure
                  layout
                  key={g.id}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  className="group cursor-pointer"
                  onClick={() => setSelected(g)}
                  data-testid={`gallery-item-${g.id}`}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={g.image}
                      alt={g.title}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-brand-teal/0 transition duration-500 group-hover:bg-brand-teal/40" />
                    <div className="absolute inset-x-0 bottom-0 translate-y-3 p-5 opacity-0 transition duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-ice">{g.catLabel}</p>
                      <p className="mt-1 font-display text-xl font-bold uppercase text-white">{g.title}</p>
                    </div>
                  </div>
                  <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 group-hover:text-white/70">
                    {g.catLabel} — {g.title}
                  </figcaption>
                </motion.figure>
              ))}
            </AnimatePresence>
          </motion.div>
          <FadeIn className="mt-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/35" data-testid="galleria-count">
              {shown.length} immagini
            </p>
          </FadeIn>
        </div>
      </section>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-brand-slate/95 p-5 backdrop-blur-md sm:p-10"
            onClick={() => setSelected(null)}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center border border-white/20 text-white transition hover:border-brand-ice hover:text-brand-ice"
              onClick={() => setSelected(null)}
              aria-label="Chiudi"
              data-testid="gallery-lightbox-close"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.figure
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 20 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={selected.image} alt={selected.title} className="max-h-[78vh] w-full object-contain" />
              <figcaption className="mt-4 flex items-baseline justify-between gap-4">
                <span className="font-display text-2xl font-bold uppercase text-white">{selected.title}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-ice">{selected.catLabel}</span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
